import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import RecipeCard from "../components/RecipeCard";
import { Loader2, ArrowUpRight, BookMarked } from "lucide-react";

const SharedCookbook = () => {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | missing

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/cookbook/shared/${shareId}`)
      .then((res) => {
        setData(res.data);
        setStatus("ready");
      })
      .catch(() => setStatus("missing"));
  }, [shareId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  if (status === "missing") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950 pt-24 px-6">
        <div className="text-center max-w-md">
          <div className="magazine-number text-9xl mb-6">Ø</div>
          <p className="font-serif-display italic text-2xl text-stone-400 mb-8">
            This cookbook has wandered off. Perhaps the link is old, or someone ate the page.
          </p>
          <Link
            to="/"
            data-testid="shared-cookbook-home"
            className="text-orange-500 uppercase text-xs tracking-[0.24em] ink-link"
          >
            Back to the kitchen
          </Link>
        </div>
      </div>
    );
  }

  const { title, author_name, recipes = [] } = data;
  const heroRecipe = recipes[0];
  const heroSrc = heroRecipe
    ? `${process.env.REACT_APP_BACKEND_URL}/api/cookbook/shared/${shareId}/og_image`
    : null;

  return (
    <div className="min-h-screen bg-stone-950 pt-16 pb-32">
      {/* Cover */}
      <section className="border-b border-stone-800">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 pt-16 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-px bg-orange-600" />
                <span className="eyebrow">A shared cookbook</span>
              </div>
              <h1
                data-testid="shared-cookbook-title"
                className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tighter text-stone-100"
              >
                {title}
              </h1>
              <div className="mt-8 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.24em] text-stone-400">
                {author_name && (
                  <span className="flex items-center gap-3">
                    <BookMarked className="w-3.5 h-3.5 text-orange-500" />
                    Curated by {author_name}
                  </span>
                )}
                <span>
                  {recipes.length} recipe{recipes.length === 1 ? "" : "s"}
                </span>
              </div>
              <Link
                to="/"
                data-testid="shared-cookbook-brand"
                className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link"
              >
                Cinder &amp; Sorrel
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {heroSrc && (
              <div className="lg:col-span-5">
                <div className="relative">
                  <img
                    src={heroSrc}
                    alt={title}
                    className="w-full aspect-[3/4] object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div className="absolute -bottom-6 -left-6 hidden md:flex flex-col bg-stone-950 border border-stone-800 p-5 w-64">
                    <span className="eyebrow mb-2">Cover dish</span>
                    <span className="font-serif-display italic text-xl text-stone-100 leading-tight">
                      {heroRecipe?.title}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recipes */}
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pt-16">
        {recipes.length === 0 ? (
          <div className="text-center py-32 border border-stone-800">
            <p className="font-serif-display italic text-2xl text-stone-400">
              This cookbook is empty — either the curator hasn&apos;t added anything yet, or every
              recipe has been quietly removed.
            </p>
          </div>
        ) : (
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="shared-cookbook-grid"
          >
            {recipes.map((r, i) => (
              <RecipeCard key={r.id} recipe={r} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SharedCookbook;
